import React, { useState, useEffect, useCallback } from 'react';
import { vpnProfilesAPI } from '../services/api';
import './ResourcePage.css';

const EMPTY_FORM = {
  name: '',
  description: '',
  vpn_type: 'wireguard',
  config_content: '',
  interface_name: '',
  vpn_pin: '',
  pritunl_profile_url: '',
};

function VpnProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Test state per profile
  const [testResults, setTestResults] = useState({}); // { [id]: { loading, success, message } }

  // Cluster list for a profile
  const [profileClusters, setProfileClusters] = useState({}); // { [id]: cluster[] }

  const fetchProfiles = useCallback(async () => {
    try {
      const res = await vpnProfilesAPI.list();
      setProfiles(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load VPN profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (profile) => {
    setEditingId(profile.id);
    setFormData({
      name: profile.name,
      description: profile.description || '',
      vpn_type: profile.vpn_type,
      config_content: '',  // never pre-fill — user must re-paste to update
      interface_name: profile.interface_name || '',
      vpn_pin: '',          // never pre-fill PIN for security
      pritunl_profile_url: profile.pritunl_profile_url || '',
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setFormData(f => ({ ...f, config_content: ev.target.result }));
    reader.readAsText(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name.trim()) { setFormError('Name is required'); return; }
    if (!editingId && !formData.config_content.trim()) { setFormError('WireGuard config content is required'); return; }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      vpn_type: formData.vpn_type,
      interface_name: formData.interface_name.trim() || undefined,
      pritunl_profile_url: formData.pritunl_profile_url.trim() || undefined,
    };
    // Only include config_content if provided (update may omit it to keep existing)
    if (formData.config_content.trim()) {
      payload.config_content = formData.config_content.trim();
    }
    // Only include PIN if provided (avoids clearing a stored PIN on edit)
    if (formData.vpn_pin.trim()) {
      payload.vpn_pin = formData.vpn_pin.trim();
    }

    setSaving(true);
    try {
      if (editingId) {
        await vpnProfilesAPI.update(editingId, payload);
      } else {
        payload.config_content = formData.config_content.trim();
        await vpnProfilesAPI.create(payload);
      }
      handleCancel();
      await fetchProfiles();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Failed to save VPN profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`Delete VPN profile "${profile.name}"? Clusters using it will lose the VPN link.`)) return;
    try {
      await vpnProfilesAPI.delete(profile.id);
      setProfiles(p => p.filter(x => x.id !== profile.id));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete profile');
    }
  };

  const handleTest = async (profile) => {
    setTestResults(r => ({ ...r, [profile.id]: { loading: true } }));
    try {
      const res = await vpnProfilesAPI.test(profile.id);
      setTestResults(r => ({ ...r, [profile.id]: { loading: false, ...res.data } }));
    } catch (err) {
      setTestResults(r => ({
        ...r,
        [profile.id]: { loading: false, success: false, message: err.response?.data?.detail || 'Test failed' },
      }));
    }
  };

  const toggleClusters = async (profile) => {
    if (profileClusters[profile.id]) {
      // toggle off
      setProfileClusters(c => { const n = { ...c }; delete n[profile.id]; return n; });
      return;
    }
    try {
      const res = await vpnProfilesAPI.listClusters(profile.id);
      setProfileClusters(c => ({ ...c, [profile.id]: res.data }));
    } catch {
      setProfileClusters(c => ({ ...c, [profile.id]: [] }));
    }
  };

  if (loading) return <div className="resource-page"><div className="page-header"><h1>VPN Profiles</h1></div><p>Loading…</p></div>;

  return (
    <div className="resource-page">
      <div className="page-header">
        <h1>VPN Profiles</h1>
        <p style={{ color: 'var(--muted)', fontSize: 14, margin: '4px 0 0' }}>
          WireGuard and OpenVPN/Pritunl profiles for accessing private clusters behind firewalls.
        </p>
        <button className="button-primary" onClick={openCreate} style={{ marginTop: 12 }}>
          + New VPN Profile
        </button>
      </div>

      {error && (
        <div className="alert alert-error" style={{ margin: '16px 0' }}>{error}</div>
      )}

      {/* Create / Edit form */}
      {showForm && (
        <div className="resource-list" style={{ padding: 24, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit VPN Profile' : 'New VPN Profile'}</h3>
          {formError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{formError}</div>}
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                  placeholder="Beta-VPN"
                  required
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>VPN Type *</label>
                <select
                  value={formData.vpn_type}
                  onChange={e => setFormData(f => ({ ...f, vpn_type: e.target.value }))}
                >
                  <option value="wireguard">WireGuard (.conf)</option>
                  <option value="openvpn">OpenVPN / Pritunl (.ovpn)</option>
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Interface Name <span style={{ fontWeight: 'normal', color: 'var(--muted)', fontSize: 12 }}>(WireGuard only)</span></label>
                <input
                  type="text"
                  value={formData.interface_name}
                  onChange={e => setFormData(f => ({ ...f, interface_name: e.target.value }))}
                  placeholder="wg0 (auto-derived if blank)"
                  maxLength={15}
                  disabled={formData.vpn_type === 'openvpn'}
                />
                <small>Auto-derived from name if blank.</small>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>

            {/* Pritunl-specific fields — shown only for OpenVPN type */}
            {formData.vpn_type === 'openvpn' && (
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>
                    Pritunl Profile URL{' '}
                    <span style={{ fontWeight: 'normal', color: 'var(--muted)', fontSize: 12 }}>(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={formData.pritunl_profile_url}
                    onChange={e => setFormData(f => ({ ...f, pritunl_profile_url: e.target.value }))}
                    placeholder="https://vpn.example.com/k/AbCdEfGh"
                  />
                  <small style={{ color: 'var(--muted)' }}>
                    If set, a fresh .ovpn is fetched automatically before every connection — no manual re-download needed.
                    Copy from the Pritunl portal: Profile → URI (use the https:// URL, not pritunl://).
                  </small>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>
                    2FA PIN{' '}
                    <span style={{ fontWeight: 'normal', color: 'var(--muted)', fontSize: 12 }}>(optional)</span>
                  </label>
                  <input
                    type="password"
                    value={formData.vpn_pin}
                    onChange={e => setFormData(f => ({ ...f, vpn_pin: e.target.value }))}
                    placeholder="Static PIN (if server requires it)"
                    autoComplete="new-password"
                  />
                  <small style={{ color: 'var(--muted)' }}>
                    {editingId ? 'Leave blank to keep existing PIN.' : 'Static PIN for Pritunl CRV1 2FA. Leave blank if not required.'}
                  </small>
                </div>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: 8 }}>
              <label>
                VPN Config File {editingId ? '(leave blank to keep existing)' : '*'}
                {' '}<span style={{ color: 'var(--muted)', fontWeight: 'normal', fontSize: 12 }}>
                  (.conf for WireGuard, .ovpn for OpenVPN/Pritunl)
                </span>
              </label>
              <input
                type="file"
                accept=".conf,.ovpn,.yaml,.yml,*"
                onChange={handleFileUpload}
                style={{ marginBottom: 8 }}
              />
              <textarea
                value={formData.config_content}
                onChange={e => setFormData(f => ({ ...f, config_content: e.target.value }))}
                placeholder={
                  formData.vpn_type === 'openvpn'
                    ? `client\ndev tun\nproto udp\nremote vpn.example.com 1194\n...`
                    : `[Interface]\nPrivateKey = ...\nAddress = 10.0.0.2/24\n\n[Peer]\nPublicKey = ...\nAllowedIPs = 192.168.1.0/24\nEndpoint = vpn.example.com:51820`
                }
                rows={10}
                className="textarea"
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
              />
              <small style={{ color: 'var(--muted)' }}>
                Paste or upload your VPN config file content. You can also paste directly from a .ovpn file.
              </small>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button type="submit" className="button-primary" disabled={saving}>
                {saving ? 'Saving…' : (editingId ? 'Update Profile' : 'Create Profile')}
              </button>
              <button type="button" className="button-secondary" onClick={handleCancel} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Profile list */}
      {profiles.length === 0 ? (
        <div className="resource-list" style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
          <p style={{ fontSize: 16, marginBottom: 8 }}>No VPN profiles yet.</p>
          <p style={{ fontSize: 14 }}>
            Add a WireGuard profile to connect private clusters that are behind a firewall.
          </p>
        </div>
      ) : (
        <div className="resource-list">
          <table className="resource-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Interface</th>
                <th>Description</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map(profile => {
                const test = testResults[profile.id];
                const clusters = profileClusters[profile.id];
                return (
                  <React.Fragment key={profile.id}>
                    <tr>
                      <td>
                        <strong>{profile.name}</strong>
                        {profile.has_pin && (
                          <span title="Has 2FA PIN" style={{
                            marginLeft: 6, background: 'var(--color-warning-50)', color: 'var(--color-warning-700)',
                            borderRadius: 4, padding: '1px 6px', fontSize: 11,
                          }}>PIN</span>
                        )}
                        {profile.pritunl_profile_url && (
                          <span title="Auto-sync enabled" className="badge accent" style={{ marginLeft: 6 }}>Auto-sync</span>
                        )}
                      </td>
                      <td>
                        <span className={profile.vpn_type === 'wireguard' ? 'badge brand' : 'badge success'}>
                          {profile.vpn_type === 'openvpn' ? 'OpenVPN/Pritunl' : 'WireGuard'}
                        </span>
                      </td>
                      <td><code style={{ fontSize: 12 }}>{profile.interface_name || '—'}</code></td>
                      <td style={{ color: 'var(--muted)', fontSize: 13 }}>{profile.description || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {new Date(profile.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            className="button-secondary"
                            style={{ fontSize: 12, padding: '4px 10px' }}
                            onClick={() => handleTest(profile)}
                            disabled={test?.loading}
                          >
                            {test?.loading ? 'Testing…' : 'Test'}
                          </button>
                          <button
                            className="button-secondary"
                            style={{ fontSize: 12, padding: '4px 10px' }}
                            onClick={() => toggleClusters(profile)}
                          >
                            {clusters ? 'Hide Clusters' : 'Clusters'}
                          </button>
                          <button
                            className="button-secondary"
                            style={{ fontSize: 12, padding: '4px 10px' }}
                            onClick={() => openEdit(profile)}
                          >
                            Edit
                          </button>
                          <button
                            className="button-danger"
                            style={{ fontSize: 12, padding: '4px 10px' }}
                            onClick={() => handleDelete(profile)}
                          >
                            Delete
                          </button>
                        </div>
                        {test && !test.loading && (
                          <div className={test.success ? 'banner success' : 'banner danger'} style={{ marginTop: 6 }}>
                            {test.success ? '✓' : '✗'} {test.message}
                            {test.interface && ` (${test.interface})`}
                          </div>
                        )}
                      </td>
                    </tr>
                    {clusters && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--bg)', padding: '8px 16px' }}>
                          {clusters.length === 0 ? (
                            <span style={{ color: 'var(--muted)', fontSize: 13 }}>No clusters using this profile.</span>
                          ) : (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {clusters.map(c => (
                                <span key={c.id} className="chip">
                                  {c.name} ({c.environment})
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default VpnProfiles;
